// A player's wallet, as TanStack Query hooks over the generated client (spec/13 §8, F-WAL-01/-02/-06/-07/-08,
// F-KIO-10) — `packages/contracts/src/routes/{wallet,wallet-topup-requests}.ts`. Read and moved from the player's
// Wallet tab (Players), and the kiosk's pay-at-the-desk requests from the Floor's "What needs you":
//
//   GET  …/players/{p}/wallet/balance                          cash, bonus, total     players:read
//   GET  …/players/{p}/wallet                                  the statement          players:read
//   POST …/players/{p}/wallet/top-ups                          top up at the counter  wallet:topup
//   POST …/players/{p}/wallet/adjustments                      credit or debit        wallet:adjust
//   POST …/players/{p}/wallet/refunds                          cash back at the desk  wallet:refund
//   GET  …/wallet/top-up-requests                              asked for at a PC      wallet:topup
//   POST …/wallet/top-up-requests/{r}/complete | /cancel       the desk settles one   wallet:topup
//
// The wallet is closed-loop and org-wide (ADR-0010): balance and statement are organisation-scoped and
// carry no venue. A counter top-up is taken **at** a venue, so it names one with `X-Clavian-Venue` and
// carries an `Idempotency-Key` — a retry with the same key answers the top-up already made and posts
// nothing twice (spec/08 §2.8). Finding the player is the one player search (`players/queries.ts`).

import type {
  ApiClient,
  KioskTopUpRequest,
  KioskTopUpRequestTender,
  WalletAdjustRequest,
  WalletBalance,
  WalletRefundRequest,
  WalletTopUpRequest,
} from '@clavian/api-client';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useApi } from '../../api/client';

/** A page of the statement. Small: the statement is read newest-first, a screenful at a time. */
const STATEMENT_PAGE = 20;

/** How often the desk's list of kiosk top-ups is read again: a player at a PC is waiting on it. */
const KIOSK_TOPUPS_REFRESH_MS = 15_000;

export const walletKeys = {
  kioskTopUps: (organizationId: string, venueId: string) => ['wallet', organizationId, 'kiosk-top-ups', venueId] as const,
  /** Everything read about one player's wallet — its balance and its statement — for a money movement to refresh. */
  player: (organizationId: string, playerId: string) => ['wallet', organizationId, playerId] as const,
  balance: (organizationId: string, playerId: string) => ['wallet', organizationId, playerId, 'balance'] as const,
  statement: (organizationId: string, playerId: string) => ['wallet', organizationId, playerId, 'statement'] as const,
};

/** A player's cash, bonus and total here — zero when they have no wallet yet (F-WAL-01). */
export function useWalletBalance(organizationId: string, playerId: string | undefined) {
  const api = useApi();
  return useQuery<WalletBalance>({
    queryKey: walletKeys.balance(organizationId, playerId ?? ''),
    enabled: playerId !== undefined,
    queryFn: () => api.getWalletBalance({ path: { organizationId, playerId: playerId ?? '' } }),
  });
}

/** The player's wallet statement here, newest first, a page at a time (F-WAL-08). */
export function useWalletStatement(organizationId: string, playerId: string | undefined) {
  const api = useApi();
  return useInfiniteQuery({
    queryKey: walletKeys.statement(organizationId, playerId ?? ''),
    enabled: playerId !== undefined,
    queryFn: ({ pageParam }) =>
      api.listPlayerWallet({
        path: { organizationId, playerId: playerId ?? '' },
        query: { limit: STATEMENT_PAGE, ...(pageParam === undefined ? {} : { cursor: pageParam }) },
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => (last.page.hasMore && last.page.cursor !== null ? last.page.cursor : undefined),
  });
}

/**
 * Top up a player's wallet at the counter (F-WAL-02). Names the venue the tender is taken at with
 * `X-Clavian-Venue` and a fresh `Idempotency-Key`; on success re-reads the balance and statement so the
 * new value and its movement appear.
 */
export function useTopUpWallet(organizationId: string, venueId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ playerId, body }: { playerId: string; body: WalletTopUpRequest }) =>
      topUp(api, organizationId, venueId, playerId, body),
    onSuccess: (_result, { playerId }) => {
      void queryClient.invalidateQueries({ queryKey: walletKeys.balance(organizationId, playerId) });
      void queryClient.invalidateQueries({ queryKey: walletKeys.statement(organizationId, playerId) });
    },
  });
}

function topUp(api: ApiClient, organizationId: string, venueId: string, playerId: string, body: WalletTopUpRequest) {
  return api.topUpWallet({
    path: { organizationId, playerId },
    headerParams: { 'idempotency-key': crypto.randomUUID() },
    // The wallet is org-wide, so a top-up names where the tender was taken as a header, not the path.
    headers: { 'X-Clavian-Venue': venueId },
    body,
  });
}

/** Re-read a player's balance and statement after a money movement, so the new value and its line appear. */
function reReadWallet(queryClient: ReturnType<typeof useQueryClient>, organizationId: string, playerId: string): void {
  void queryClient.invalidateQueries({ queryKey: walletKeys.balance(organizationId, playerId) });
  void queryClient.invalidateQueries({ queryKey: walletKeys.statement(organizationId, playerId) });
}

/**
 * Credit or debit a player's wallet cash with a reason (F-WAL-07, `wallet:adjust`). Money moves through the
 * ledger server-side; the amount is capped by the role's limit and a debit cannot overdraw. Names the venue
 * with `X-Clavian-Venue` and carries a fresh `Idempotency-Key`; re-reads the balance and statement on success.
 */
export function useAdjustWallet(organizationId: string, venueId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ playerId, body }: { playerId: string; body: WalletAdjustRequest }) =>
      api.adjustWallet({
        path: { organizationId, playerId },
        headerParams: { 'idempotency-key': crypto.randomUUID() },
        headers: { 'X-Clavian-Venue': venueId },
        body,
      }),
    onSuccess: (_result, { playerId }) => reReadWallet(queryClient, organizationId, playerId),
  });
}

/**
 * Refund a player's wallet cash to a counter tender with a reason (F-WAL-06, `wallet:refund`). Reverses cash
 * (never bonus) up to what the wallet holds, through the ledger. Names the venue with `X-Clavian-Venue` and a
 * fresh `Idempotency-Key`; re-reads the balance and statement on success.
 */
export function useRefundWallet(organizationId: string, venueId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ playerId, body }: { playerId: string; body: WalletRefundRequest }) =>
      api.refundWallet({
        path: { organizationId, playerId },
        headerParams: { 'idempotency-key': crypto.randomUUID() },
        headers: { 'X-Clavian-Venue': venueId },
        body,
      }),
    onSuccess: (_result, { playerId }) => reReadWallet(queryClient, organizationId, playerId),
  });
}

/** The top-ups players asked for at a PC to pay at this venue's desk, oldest first (F-KIO-10, `wallet:topup`). */
export function useKioskTopUps(organizationId: string, venueId: string, enabled = true) {
  const api = useApi();
  return useQuery<readonly KioskTopUpRequest[]>({
    queryKey: walletKeys.kioskTopUps(organizationId, venueId),
    enabled,
    refetchInterval: KIOSK_TOPUPS_REFRESH_MS,
    queryFn: async () =>
      (await api.listWalletTopUpRequests({ path: { organizationId }, headers: { 'X-Clavian-Venue': venueId } })).data,
  });
}

/**
 * Take a kiosk top-up's money at the counter (F-KIO-10): the server tops the wallet up by the counter tender through the
 * ledger, with the offer's bonus, and closes the request. A fresh `Idempotency-Key` per tap; a retry the server has seen
 * answers the top-up already made.
 */
export function useCompleteKioskTopUp(organizationId: string, venueId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ requestId, tender }: { requestId: string; tender: KioskTopUpRequestTender }) =>
      api.completeWalletTopUpRequest({
        path: { organizationId, requestId },
        headerParams: { 'idempotency-key': crypto.randomUUID() },
        headers: { 'X-Clavian-Venue': venueId },
        body: { tender },
      }),
    onSuccess: (request) => {
      void queryClient.invalidateQueries({ queryKey: walletKeys.kioskTopUps(organizationId, venueId) });
      reReadWallet(queryClient, organizationId, request.playerId);
    },
    onError: () => void queryClient.invalidateQueries({ queryKey: walletKeys.kioskTopUps(organizationId, venueId) }),
  });
}

/** Cancel a kiosk top-up that will not be paid (F-KIO-10): nothing is credited. */
export function useCancelKioskTopUp(organizationId: string, venueId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (requestId: string) =>
      api.cancelWalletTopUpRequest({ path: { organizationId, requestId }, headers: { 'X-Clavian-Venue': venueId } }),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: walletKeys.kioskTopUps(organizationId, venueId) }),
  });
}
